// screens/marketplace/RichText.jsx
import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import { useTheme } from "../../hooks/useTheme";

/**
 * Render sanitized HTML from the course description field.
 *
 * The admin editor (Batch 3) writes HTML. Anything the API returns is
 * treated as untrusted and passed through DOMPurify before rendering.
 * We also strip a small allowlist of unsafe attributes and enforce
 * `target="_blank"` on external links via a post-process hook.
 *
 * Usage:
 *   <RichText html={course.description} />
 *   <RichText html={course.description} emptyLabel="No description yet" />
 */

const ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "h2",
  "h3",
  "h4",
  "ul",
  "ol",
  "li",
  "blockquote",
  "code",
  "pre",
  "a",
  "hr",
];

const ALLOWED_ATTR = ["href", "title", "target", "rel"];

export default function RichText({ html, emptyLabel = "—" }) {
  const { colors } = useTheme();

  const clean = useMemo(() => {
    if (!html || typeof html !== "string") return "";
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      // Force external links to be safe
      ADD_ATTR: ["target", "rel"],
      FORBID_TAGS: ["script", "style", "iframe", "object", "embed"],
      FORBID_ATTR: ["onerror", "onload", "onclick", "style", "class"],
    });
  }, [html]);

  if (!clean || clean.trim() === "") {
    return (
      <p
        style={{
          margin: 0,
          fontSize: 14,
          color: colors.secondaryText,
          fontStyle: "italic",
        }}
      >
        {emptyLabel}
      </p>
    );
  }

  return (
    <div
      className="rich-text"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: clean }}
      style={{
        fontSize: 14.5,
        lineHeight: 1.7,
        color: colors.text,
      }}
    />
  );
}

// Scoped typography for the rendered HTML. Kept minimal — only what
// admins can produce from the Batch-3 toolbar.
if (
  typeof window !== "undefined" &&
  !document.getElementById("rich-text-style")
) {
  const el = document.createElement("style");
  el.id = "rich-text-style";
  el.textContent = `
    .rich-text p { margin: 0 0 12px; }
    .rich-text p:last-child { margin-bottom: 0; }
    .rich-text h2 {
      font-size: 19px;
      font-weight: 800;
      letter-spacing: -0.01em;
      margin: 22px 0 10px;
      color: inherit;
    }
    .rich-text h3 {
      font-size: 16px;
      font-weight: 700;
      margin: 18px 0 8px;
      color: inherit;
    }
    .rich-text h4 {
      font-size: 14.5px;
      font-weight: 700;
      margin: 16px 0 6px;
      color: inherit;
    }
    .rich-text h2:first-child,
    .rich-text h3:first-child,
    .rich-text h4:first-child { margin-top: 0; }
    .rich-text ul,
    .rich-text ol { margin: 0 0 12px; padding-left: 22px; }
    .rich-text li { margin-bottom: 4px; }
    .rich-text blockquote {
      margin: 14px 0;
      padding: 10px 16px;
      border-left: 3px solid currentColor;
      opacity: 0.85;
      font-style: italic;
    }
    .rich-text code {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 0.9em;
      padding: 1px 5px;
      border-radius: 5px;
      background: rgba(127, 127, 127, 0.12);
    }
    .rich-text pre {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 0.9em;
      padding: 12px 14px;
      border-radius: 10px;
      background: rgba(127, 127, 127, 0.12);
      overflow-x: auto;
      margin: 14px 0;
    }
    .rich-text a {
      color: inherit;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    .rich-text hr {
      border: none;
      border-top: 1px solid rgba(127, 127, 127, 0.25);
      margin: 18px 0;
    }
  `;
  document.head.appendChild(el);
}